'use client';

import { useReducedMotion } from 'motion/react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import { cn } from '@/lib/utils';

const HOVER_REVEAL_DELAY_MS = 300;
const AUTO_REVEAL_HOLD_MS = 700;
const OVERFLOW_FADE_WIDTH_PX = 16;
const REVEAL_SPEED_PX_PER_SECOND = 48;
const MIN_REVEAL_DURATION_MS = 800;
const MAX_REVEAL_DURATION_MS = 4200;
const EMPTY_SUBSCRIBE = (): (() => void) => () => undefined;

function useOverflowDistance(
  viewport: HTMLElement | null,
  content: HTMLElement | null
): number {
  const subscribe = useCallback(
    (onStoreChange: () => void): (() => void) => {
      if (!viewport || !content || typeof ResizeObserver === 'undefined') {
        return EMPTY_SUBSCRIBE();
      }

      const observer = new ResizeObserver(onStoreChange);
      observer.observe(viewport);
      observer.observe(content);
      return () => observer.disconnect();
    },
    [content, viewport]
  );
  const getSnapshot = useCallback(
    () =>
      viewport && content
        ? Math.max(0, Math.ceil(content.scrollWidth - viewport.clientWidth))
        : 0,
    [content, viewport]
  );

  return useSyncExternalStore(subscribe, getSnapshot, () => 0);
}

function revealDurationMs(overflowDistance: number): number {
  return Math.min(
    MAX_REVEAL_DURATION_MS,
    Math.max(
      MIN_REVEAL_DURATION_MS,
      Math.round((overflowDistance / REVEAL_SPEED_PX_PER_SECOND) * 1000)
    )
  );
}

export function HorizontalOverflowHoverReveal({
  children,
  className,
  contentClassName,
  autoReveal = false,
  revealed = false,
  ellipsis = false,
}: {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  autoReveal?: boolean;
  revealed?: boolean;
  /** At rest, end overflowing text with "…" instead of a fade. */
  ellipsis?: boolean;
}) {
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
  const [content, setContent] = useState<HTMLDivElement | null>(null);
  const [autoRevealedDistance, setAutoRevealedDistance] = useState<
    number | null
  >(null);
  const [isHoverRevealed, setIsHoverRevealed] = useState(false);
  const revealTimerRef = useRef<number | null>(null);
  const prefersReducedMotion = useReducedMotion() ?? false;
  const overflowDistance = useOverflowDistance(viewport, content);
  const isOverflowing = overflowDistance > 0;
  const canReveal = isOverflowing;
  const shouldReveal =
    canReveal &&
    (revealed ||
      (!prefersReducedMotion &&
        (isHoverRevealed ||
          (autoReveal && autoRevealedDistance === overflowDistance))));
  const restingEllipsis = ellipsis && !shouldReveal;
  const maskImage =
    isOverflowing && !restingEllipsis
      ? shouldReveal
        ? `linear-gradient(to right, transparent, black ${OVERFLOW_FADE_WIDTH_PX}px)`
        : `linear-gradient(to right, black calc(100% - ${OVERFLOW_FADE_WIDTH_PX}px), transparent)`
      : undefined;

  const clearRevealTimer = useCallback(() => {
    if (revealTimerRef.current === null) return;
    window.clearTimeout(revealTimerRef.current);
    revealTimerRef.current = null;
  }, []);

  useEffect(() => clearRevealTimer, [clearRevealTimer]);

  useEffect(() => {
    if (!autoReveal || !canReveal || prefersReducedMotion) return;

    const revealTimer = window.setTimeout(
      () => setAutoRevealedDistance(overflowDistance),
      HOVER_REVEAL_DELAY_MS
    );
    const resetTimer = window.setTimeout(
      () => setAutoRevealedDistance(null),
      HOVER_REVEAL_DELAY_MS +
        revealDurationMs(overflowDistance) +
        AUTO_REVEAL_HOLD_MS
    );
    return () => {
      window.clearTimeout(revealTimer);
      window.clearTimeout(resetTimer);
    };
  }, [autoReveal, canReveal, overflowDistance, prefersReducedMotion]);

  const handleMouseEnter = () => {
    if (!canReveal || prefersReducedMotion) return;
    clearRevealTimer();
    revealTimerRef.current = window.setTimeout(() => {
      revealTimerRef.current = null;
      setIsHoverRevealed(true);
    }, HOVER_REVEAL_DELAY_MS);
  };

  const handleMouseLeave = () => {
    clearRevealTimer();
    setIsHoverRevealed(false);
  };

  return (
    <div
      ref={setViewport}
      data-horizontal-overflow-viewport
      data-overflowing={isOverflowing}
      data-revealed={shouldReveal}
      className={cn('block min-w-0 overflow-hidden', className)}
      style={{ maskImage, WebkitMaskImage: maskImage }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
    >
      <div
        ref={setContent}
        data-horizontal-overflow-content
        className={cn(
          // scrollWidth still reports the full text while truncated, so the
          // overflow measurement is unaffected by the resting ellipsis.
          restingEllipsis
            ? 'block max-w-full overflow-hidden text-ellipsis whitespace-nowrap'
            : 'inline-block min-w-max whitespace-nowrap',
          contentClassName
        )}
        style={{
          transform: shouldReveal
            ? `translate3d(-${overflowDistance}px, 0, 0)`
            : 'translate3d(0px, 0, 0)',
          transitionDuration:
            shouldReveal && !prefersReducedMotion
              ? `${revealDurationMs(overflowDistance)}ms`
              : '0ms',
          transitionProperty: 'transform',
          transitionTimingFunction: 'linear',
          willChange:
            shouldReveal && !prefersReducedMotion ? 'transform' : undefined,
        }}
      >
        {children}
      </div>
    </div>
  );
}
